import React, { useEffect } from 'react';

import QRCode from 'react-qr-code';
import { useDispatch } from 'react-redux';

import { theme } from '@waglabs/base-theme';
import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import {
  AppDownloadButton,
  Distribution,
} from '@waglabs/get-app-button';
import { Image } from '@waglabs/image-next';
import { Typography } from '@waglabs/typography';

import { Container } from '../../../../../../components/Container';
import { AppDownloadLink } from '../../../../../../constants';
import { useBranchCreateLink } from '../../../../../../hooks/useBranchCreateLink';
import { isInEmbed } from '../../../../../../hooks/useIsInEmbed';
import { petsSlice } from '../../../../../../slices/pets';

import successCheckIcon from './assets/success-check.svg';

export type CompleteProps = {
  walkerName: string;
  text?: string;
};

export const Complete = ({
  walkerName,
  text = walkerName && `Download the Wag! App to start booking a service with ${walkerName}.`,
}: CompleteProps) => {
  const branchLink = useBranchCreateLink();
  const dispatch = useDispatch();

  useEffect(() => () => {
    /**
     * Clear inactive pets after this screen
     * The moment the user arrived on this page, we've already save the pet in the BE
     * No need for us to store in the FE the inactive pets
     */
    dispatch(
      petsSlice.actions.clearInactive(),
    );
  }, [dispatch]);

  return (
    <Container
      display="flex"
      flexDirection="column"
      textAlign="center"
      justifyContent="center"
      p={['20px', null, '40px']}
    >
      <Image
        src={successCheckIcon}
        alt="success-check-icon"
        alignSelf="center"
        mb="20px"
        width="90px"
      />

      <Typography
        color={theme.colors.charcoal}
        fontFamily="muliBold"
        fontSize="30px"
      >
        Almost there!
      </Typography>

      <Typography
        color={theme.colors.dimGray}
      >
        {text}
      </Typography>

      <Box
        display={isInEmbed ? ['none', 'grid'] : ['none', null, 'grid']}
        gridTemplateColumns="max-content 1fr"
        alignSelf="center"
        borderColor={theme.colors.lightGrey}
        borderStyle="solid"
        borderWidth="1px"
        borderRadius="8px"
        alignItems="center"
        maxWidth="336px"
        p="12px"
      >
        {branchLink && (
          <QRCode
            value={branchLink}
            width="130px"
            height="130px"
            size={130}
            fgColor={theme.colors.caribbeanGreen}
            {...{
              alt: 'download-app-qr-code',
            }}
          />
        )}

        <Typography
          color={theme.colors.charcoal}
          fontSize="16px"
          my={0}
          ml="16px"
          textAlign="left"
        >
          Scan to download Wag! Pet Parent app
        </Typography>
      </Box>

      <Flex
        alignSelf="center"
        display={isInEmbed ? ['flex', 'none'] : ['flex', null, 'none']}
        justifyContent="center"
      >
        <AppDownloadButton
          distribution={Distribution.AppStore}
          href={AppDownloadLink.Login.AppStore}
          target="_self"
          width="150px"
          mr="20px"
        />

        <AppDownloadButton
          distribution={Distribution.PlayStore}
          href={AppDownloadLink.Login.PlayStore}
          target="_self"
          width="150px"
        />
      </Flex>
    </Container>
  );
};
